import Vue from 'vue'
import { store } from '@/apps/default/store'
import Api from '@/providers/Api'
import * as _ from 'lodash'

/**
 * Service to support dynamic breadcrumbs.
 * Breadcrumbs are specified in the router as meta.crumbs for each route.
 * In some cases the URL and/or the label for a crumb need to be generated based on one or more route params, e.g.:
 *
 * meta: {
 *     crumbs: [
 *         {
 *             to: '/practitioner/admin/practiceId/:practiceId',
 *             label: 'Manage Practitioners for :practiceName'
 *         }
 *
 * This service (called by the router) checks for these cases and makes the appropriate substitutions.
 * The current breadcrumbs are represented in the Vuex store, and displayed using the BackNav component.
 */

export default {

    // called by the beforeEach guard in the router - performs any required substitutions based on the given route
    renderCrumbs (route) {
        // construct breadcrumb links based on meta.crumbs spec in route
        const metaCrumbs = route.meta.crumbs
        let crumbs = []
        if (_.isArray(metaCrumbs)) {
            // if it's an array, include the first-level link for free
            crumbs = _.concat(
                [{ to: '/', label: 'Dashboard' }],
                _.cloneDeep(metaCrumbs) // important: don't perform substitutions on meta crumbs!
            )
        }
        store.state.breadcrumbs = crumbs

        // perform substitutions on link URLs and labels
        if (metaCrumbs) {
            _.each(metaCrumbs, (mc, i) => {
                const crumb = store.state.breadcrumbs[i + 1] // skip initial dashboard link

                // substitute any parameter tokens (e.g. ':participantId')
                // in the crumb 'to' with the appropriate value from the route
                _.each(_.keys(route.params), (paramName) => {
                    if (crumb.to) {
                        crumb.to = crumb.to.replace(`:${paramName}`, route.params[paramName])
                    }
                })

                // substitute any name tokens (e.g. ':practiceName')
                // in the crumb label with a name retrieved via the API
                _.each(this.replacements, (r) => {
                    const nameToken = `:${r.table}Name`   // e.g. 'practiceName'
                    const idKey = `${r.table}Id`          // e.g. 'practiceId'
                    if (crumb.label && crumb.label.indexOf(nameToken) != -1) {
                        Api.getItem(this, r.table, route.params[idKey])
                            .then((item) => {
                                    Vue.set(crumb, 'label', crumb.label.replace(nameToken, item[r.nameField]))
                                },
                                (error) => {
                                    // if something went wrong, replace token with the defaultLabel
                                    Vue.set(crumb, 'label', crumb.label.replace(nameToken, r.defaultLabel))
                                })
                    }
                })
            })
        }
    },

    // list of ID/name tokens that need to be replaced in breadcrumb links
    replacements: [
        // for tokens :practiceId / :practiceName
        { table: 'practice', nameField: 'name', defaultLabel: 'Practice' },
        // for tokens :participantId / :participantName
        { table: 'participant', nameField: 'fullname', defaultLabel: 'Patient' },
        // for tokens :companyId / :companyName
        { table: 'company', nameField: 'name', defaultLabel: 'Company' }
    ],
}
